import {
	MaterialObject,
	OrderMaterial,
	MaterialShape,
	MaterialOrderResults,
	MaterialResults,
	MaterialType,
	MaterialVendor,
	MaterialSource,
} from '../../interfaces/Material.Interface';
import {
	IMaterialResults,
	MaterialRack,
	MaterialShelf,
} from '../../interfaces/MaterialInventory.interface';
import { MaterialRemenants } from '../../interfaces/MaterialRemenat';
import { GetQueryId } from '../../interfaces/Ordel.Interface';
import { api } from '../slices/api';
import { notification } from 'antd';

/**
 * Fetch inventory/locations/ following server pagination until exhausted and
 * return one merged {count, results, next: null} payload (PTB-6412: the
 * catalog is 875+ locations but the server caps pages at 500, and consumers
 * treat one page as the whole catalog). Non-paginated response shapes — e.g.
 * the material_length_ids candidate mode returning by_material_length — are
 * returned untouched after the first request. Caller params are preserved on
 * every page request; MAX_PAGES bounds a runaway loop.
 */
export const fetchAllInventoryLocationPages = async (
	fetchWithBQ: (args: { url: string; params?: Record<string, any> }) => Promise<{ data?: any; error?: any }>,
	params: Record<string, any> = {}
) => {
	const MAX_PAGES = 50;
	const first = await fetchWithBQ({ url: 'inventory/locations/', params });
	if (first.error) return { error: first.error };
	const firstData: any = first.data ?? {};
	if (!Array.isArray(firstData.results) || !firstData.next) {
		return { data: firstData };
	}
	const results = [...firstData.results];
	let next = firstData.next;
	let page = 2;
	while (next && page <= MAX_PAGES) {
		const res = await fetchWithBQ({
			url: 'inventory/locations/',
			params: { ...params, page: String(page) },
		});
		if (res.error) return { error: res.error };
		const pageData: any = res.data ?? {};
		results.push(...(pageData.results ?? []));
		next = pageData.next;
		page += 1;
	}
	return { data: { ...firstData, results, next: null } };
};

/**
 * Tags to invalidate after a reservation write (PTB-5965). Reservations change
 * the order line item's total_reserved_scrap_quantity, and the reservation modal
 * nets the remaining scrap allowance against it — a stale cache entry pre-fills
 * scrap that was already allocated. Failures invalidate too: the server rejects
 * writes whose netting is out of date, so the retry needs the fresh total.
 * Without an id (e.g. deletes from the inventory screens) the whole type is
 * invalidated instead.
 */
const orderLineItemTags = (orderLineItemId?: string | null) =>
	orderLineItemId
		? [{ type: 'OrderLineItem' as const, id: orderLineItemId }]
		: ['OrderLineItem' as const];

interface MaterialTypeQuery {
	material_category: string | null;
}

interface CalculateRequiredLengthsProps {
	material_cut_length: number;
	material_cut_quantity: number;
	saw_kerf?: number;
	whole_bar_length?: number;
}

interface CalculateScrapQuantityProps {
	quantity: number;
	shape?: string | null;
	material_cut_length?: number | null;
	height?: number | string | null;
	width?: number | string | null;
	outer_diameter?: number | string | null;
	inner_diameter?: number | string | null;
	wall_thickness?: number | string | null;
	cost_per_cubic_inch?: number | string | null;
	material_id?: string | null;
	material_type_id?: string | null;
}

interface ScrapTier {
	index: number;
	lower: number;
	upper: number | null;
}

interface CalculateScrapQuantityResult {
	quantity: number;
	cost_per_piece: number | null;
	estimated_scrap_quantity: number;
	used_fallback: boolean;
	fallback_reason: string | null;
	fallback_percent?: number;
	quantity_tier?: ScrapTier | null;
	cost_tier?: ScrapTier | null;
	quantity_tiers?: number[];
	cost_tiers?: number[];
	scrap_grid?: number[][];
}

interface UniqueInventoryMaterialsQueryParams {
	shape: string;
	category: string;
	types: string[];
	height?: string;
	width?: string;
	outer_diameter?: string;
}

// Define a service using a base URL and expected endpoints
export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getMaterialTypes: builder.query<MaterialType[], MaterialTypeQuery>({
			query: ({ material_category }) =>
				material_category
					? `material/material-types/?material_category=${material_category}`
					: 'material/material-types/',
		}),

		getMaterialVendors: builder.query<MaterialVendor[], void>({
			query: () => 'material/material-vendors/',
		}),

		getMaterialShapes: builder.query<MaterialShape[], void>({
			query: () => 'material/material-shapes/',
		}),

		getMaterialCategories: builder.query<MaterialShape[], void>({
			query: () => 'material/material-categories/',
		}),

		getMaterialRacks: builder.query<MaterialRack[], void>({
			query: () => 'material/material-racks/',
		}),

		getMaterialShelves: builder.query<MaterialShelf[], void>({
			query: () => 'material/material-shelves/',
		}),

		getInventoryLocations: builder.query({
			// PTB-6412: the locations list is server-paginated at 500/page, but every
			// consumer treats data.results as the FULL catalog (dropdowns, filters,
			// move/split candidates context) — so locations beyond page 1 were
			// unselectable and unsearchable. Follow the pagination and return the
			// whole set; candidate-mode responses (material_length_ids) and other
			// non-paginated shapes pass through untouched.
			queryFn: async (params, _api, _extraOptions, fetchWithBQ) =>
				fetchAllInventoryLocationPages(fetchWithBQ, params ?? {}),
		}),

		getSplitLocationCandidates: builder.query({
			query: (params: { material_length_id: string; lengths: string }) => ({
				url: `inventory/locations/split-candidates/`,
				params,
			}),
		}),

		getMaterialOrders: builder.query<
			MaterialOrderResults,
			{
				search?: string;
				page?: number;
				ordering?: string;
				hide_draft_and_used_materials?: boolean;
			} | void
		>({
			query: (args) => {
				if (args) {
					return {
						url: 'material/material-orders',
						params: args,
					};
				}
				return 'material/material-orders/';
			},
			providesTags: ['getMaterialOrders'],
		}),

		getMaterialsOrderSearch: builder.mutation({
			query: ({ search, page }) => ({
				url:
					`material/material-orders` +
					(page ? `?page=${page}` : '') +
					(search ? `${page ? '&' : '?'}search=${search}` : ''),
				method: 'GET',
			}),
		}),

		getMaterialOrderById: builder.query<OrderMaterial, GetQueryId>({
			query: ({ id }: GetQueryId) => `material/material-orders/${id}/`,
		}),

		getMaterial: builder.query<
			MaterialResults,
			{
				search?: string;
				page?: number;
				ordering?: string;
				hide_draft_and_used_materials?: boolean;
			} | void
		>({
			query: (args) => {
				if (args) {
					return {
						url: 'material/material-order-line-items/',
						params: args,
					};
				}
				return 'material/material-order-line-items/';
			},
		}),

		addMaterialOrderLineItem: builder.mutation({
			query: (values) => ({
				url: 'material/material-order-line-items/',
				method: 'POST',
				body: values,
			}),
		}),

		addMaterialOrder: builder.mutation({
			query: (values) => ({
				url: 'material/material-orders/',
				method: 'POST',
				body: values,
			}),
		}),

		addMaterialOrderDraft: builder.mutation({
			query: (values) => ({
				url: 'material/material-orders/create-draft-from-material-request/',
				method: 'POST',
				body: values,
			}),
		}),

		addMaterialOrderConfirmation: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-orders/${id}/order-confirmation/`,
				method: 'POST',
				body: values,
			}),
		}),

		updateMaterialOrderLineItem: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-order-line-items/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: ['getMaterialRemnant'],
		}),

		uploadMaterialDocumentFileInMaterialOrderLineItem: builder.mutation({
			query: ({ id, formData }) => ({
				url: `material/material-order-line-items/${id}/upload-document/`,
				method: 'POST',
				body: formData,
			}),
		}),

		getMaterialDetail: builder.query<MaterialObject[], void>({
			query: (id) => `material/material-order-line-items/${id}/`,
		}),

		toggleMerge: builder.mutation({
			query: ({ material_order_id, vendor_part_number, is_checked }) => ({
				url: `material/material-orders/${material_order_id}/toggle-merge/`,
				method: 'POST',
				body: { material_order_id, vendor_part_number, is_checked },
			}),
			invalidatesTags: ['MaterialOrder'],
		}),

		addMaterialOrderLineItemFromMaterialRequest: builder.mutation({
			query: ({ material_order_id, material_order_items }) => ({
				url: `material/material-orders/${material_order_id}/add-items-from-material-request/`,
				method: 'POST',
				body: {
					material_order_items: material_order_items,
				},
			}),
		}),

		addMaterialOrderLineItemFromVendorMaterial: builder.mutation({
			query: ({ material_order_id, material_order_items }) => ({
				url: `material/material-orders/${material_order_id}/add-items-from-vendor-material/`,
				method: 'POST',
				body: {
					material_order_items: material_order_items,
				},
			}),
		}),

		deleteMaterialOrderLineItem: builder.mutation({
			query: ({ id }) => ({
				url: `material/material-order-line-items/${id}/delete/`,
				method: 'DELETE',
			}),
		}),

		/**
		 * Takes one line off a draft order. A line built from material requests
		 * is one bar of a cut plan, so the server sends those requests back to
		 * the pool and packs the rest of the order again -- the response is the
		 * rebuilt order, not a deleted row.
		 */
		removeMaterialOrderLineItemFromDraft: builder.mutation({
			query: ({ material_order_id, line_item_id }) => ({
				url: `material/material-orders/${material_order_id}/remove-from-draft/`,
				method: 'POST',
				body: { line_item_id },
			}),
			invalidatesTags: ['MaterialOrder'],
		}),

		searchMaterial: builder.mutation({
			query: ({ search, page, hide_draft_and_used_materials = false }) => ({
				url:
					`material/material-order-line-items/?hide_draft_and_used_materials=${hide_draft_and_used_materials}` +
					(page ? `&page=${page}` : '') +
					(search ? `&search=${search}` : ''),
				method: 'GET',
			}),
		}),

		addMaterialRemnant: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-order-line-items/${id}/create-remnant/`,
				params: { hide_draft_and_used_materials: true },
				method: 'POST',
				body: values,
			}),
			invalidatesTags: ['getMaterialRemnant'],
		}),

		getMaterialRemnant: builder.query<
			MaterialRemenants,
			{
				search?: string;
				page?: number;
				ordering?: string;
				hide_draft_and_used_materials?: boolean;
				hide_all_unavailable_materials?: boolean;
			} | void
		>({
			query: (args) => {
				if (args) {
					return {
						url: 'material/material-order-line-items/',
						params: args,
					};
				}

				return {
					url: 'material/material-order-line-items/',
					params: { hide_draft_and_used_materials: true },
				};
			},
			providesTags: ['getMaterialRemnant'],
		}),

		getPOPDF: builder.mutation({
			query: (values) => ({
				url: `material/material-orders/purchase-order-pdf/`,
				method: 'POST',
				body: values,
				responseHandler: async (response) => {
					if (response.status === 200) {
						window.open(
							window.URL.createObjectURL(await response.blob()),
							'_blank'
						);
					} else {
						const errorData = await response.json();
						notification.error({
							duration: 10,
							message: 'Error',
							description: errorData['message'],
							placement: 'topRight',
						});
					}
				},
				cache: 'no-cache',
			}),
		}),

		updateMaterialOrder: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-orders/${id}/update-draft-material-order/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: ['getMaterialOrders'],
		}),

		deleteMaterialOrder: builder.mutation({
			query: ({ id }) => ({
				url: `material/material-orders/${id}/`,
				method: 'DELETE',
			}),
		}),

		patchMaterialOrder: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-orders/${id}/`,
				method: 'PATCH',
				body: values,
			}),
		}),

		getMaterials: builder.query<
			IMaterialResults,
			{
				search?: string;
				page?: number;
				ordering?: string;
				shape?: string;
				category?: string;
				types?: string | string[];
				height?: string;
				width?: string;
				outer_diameter?: string;
				inner_diameter?: string;
				wall_thickness?: string;
				height__gte?: string;
				height__lte?: string;
				width__gte?: string;
				width__lte?: string;
				outer_diameter__gte?: string;
				outer_diameter__lte?: string;
				inner_diameter__gte?: string;
				inner_diameter__lte?: string;
				wall_thickness__gte?: string;
				wall_thickness__lte?: string;
				min_total_available_length?: number | string;
				has_certificate?: boolean | string;
				vendors?: string;
			} | void
		>({
			query: (args) => {
				if (args) {
					return {
						url: 'material/materials/',
						params: args,
					};
				}
				return 'material/materials/';
			},
		}),

		getMaterialById: builder.query({
			query: ({ id }) => `material/materials/${id}/`,
		}),

		getMaterialGroupedByLength: builder.query({
			query: ({ material_id }) => `material/materials/${material_id}/grouped/`,
		}),

		getUniqueInventoryMaterialHeights: builder.query<
			string[],
			UniqueInventoryMaterialsQueryParams
		>({
			query: (params) => {
				return {
					url: `material/materials/unique-heights/`,
					params: params,
				};
			},
		}),

		getUniqueInventoryMaterialWidths: builder.query<
			string[],
			UniqueInventoryMaterialsQueryParams
		>({
			query: (params) => {
				return {
					url: `material/materials/unique-widths/`,
					params: params,
				};
			},
		}),

		getUniqueInventoryMaterialOuterDiameters: builder.query<
			string[],
			UniqueInventoryMaterialsQueryParams
		>({
			query: (params) => {
				return {
					url: `material/materials/unique-outer-diameters/`,
					params: params,
				};
			},
		}),

		getUniqueInventoryMaterialInnerDiameters: builder.query<
			string[],
			UniqueInventoryMaterialsQueryParams
		>({
			query: (params) => {
				return {
					url: `material/materials/unique-inner-diameters/`,
					params: params,
				};
			},
		}),

		getUniqueInventoryMaterialWallThicknesses: builder.query<
			string[],
			UniqueInventoryMaterialsQueryParams
		>({
			query: (params) => {
				return {
					url: `material/materials/unique-wall-thicknesses/`,
					params: params,
				};
			},
		}),

		addMaterial: builder.mutation({
			query: ({ values, idempotencyKey }) => ({
				url: `material/materials/`,
				method: 'POST',
				body: values,
				...(idempotencyKey
					? { headers: { 'Idempotency-Key': idempotencyKey } }
					: {}),
			}),
		}),

		updateMaterial: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/materials/${id}/`,
				method: 'PATCH',
				body: values,
			}),
		}),

		reserveMaterial: builder.mutation({
			query: ({ id, values, idempotencyKey }) => ({
				url: `material/materials/${id}/reserve-material/`,
				method: 'POST',
				body: values,
				...(idempotencyKey
					? { headers: { 'Idempotency-Key': idempotencyKey } }
					: {}),
			}),
			invalidatesTags: (_result, _error, arg) =>
				orderLineItemTags(arg?.values?.order_line_item),
		}),

		getMaterialLengthById: builder.query({
			query: ({ id }) => `material/material-lengths/${id}/`,
		}),

		addMaterialLengths: builder.mutation({
			query: (values) => ({
				url: `material/material-lengths/`,
				method: 'POST',
				body: values,
			}),
		}),

		updateMaterialLength: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-lengths/${id}/`,
				method: 'PATCH',
				body: values,
			}),
		}),

		deleteMaterialLength: builder.mutation({
			query: ({ id }) => ({
				url: `material/material-lengths/${id}/`,
				method: 'DELETE',
			}),
		}),

		recordAttributionCost: builder.mutation({
			query: (body: {
				operator_id: string;
				modifications: Array<{
					material_length_id: string;
					attribute_cost?: string;
					new_length?: number;
					difference?: number;
					associated_job_id?: string | null;
					inventory_location_id?: string | null;
				}>;
			}) => ({
				url: 'material/material-lengths/record-attribution-cost/',
				method: 'POST',
				body,
			}),
		}),

		splitMaterialLength: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-lengths/${id}/split/`,
				method: 'POST',
				body: values,
			}),
			invalidatesTags: ['MaterialOrder'],
		}),



		getMaterialReservations: builder.query({
			query: (params) => {
				return {
					url: `material/material-reservations`,
					params,
				};
			},
		}),

		getGroupedMaterialReservations: builder.query({
			query: (params) => {
				return {
					url: `material/material-reservations/grouped`,
					params,
				};
			},
		}),

		getMaterialReservationById: builder.query({
			query: ({ id }) => {
				return {
					url: `material/material-reservations/${id}/`,
				};
			},
		}),

		addMaterialReservation: builder.mutation({
			query: ({ values, idempotencyKey }) => ({
				url: `material/material-reservations/`,
				method: 'POST',
				body: values,
				...(idempotencyKey
					? { headers: { 'Idempotency-Key': idempotencyKey } }
					: {}),
			}),
			invalidatesTags: (_result, _error, arg) =>
				orderLineItemTags(arg?.values?.order_line_item),
		}),

		deleteMaterialReservation: builder.mutation({
			query: ({ id }) => ({
				url: `material/material-reservations/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, arg) =>
				orderLineItemTags(arg?.orderLineItemId),
		}),

		updateMaterialReservation: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-reservations/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (_result, _error, arg) =>
				orderLineItemTags(arg?.orderLineItemId ?? arg?.values?.order_line_item),
		}),

		updateMaterialReservationPriority: builder.mutation({
			query: ({ id, priority }) => ({
				url: `material/material-reservations/${id}/prioritize/`,
				method: 'POST',
				body: { priority },
			}),
		}),

		updateMaterialReservationCutStatus: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/material-reservations/${id}/update-status/`,
				method: 'POST',
				body: values,
			}),
		}),

		updateMultipleMaterialReservationCutStatuses: builder.mutation({
			query: (values) => ({
				url: `material/material-reservations/group-statuses/`,
				method: 'PATCH',
				body: values,
			}),
		}),

		updateMultipleMaterialReservationsToCutStatus: builder.mutation({
			query: ({ values, idempotencyKey }) => ({
				url: `material/material-reservations/cut-multiple-reservations/`,
				method: 'PATCH',
				body: values,
				...(idempotencyKey
					? { headers: { 'Idempotency-Key': idempotencyKey } }
					: {}),
			}),
		}),

		uploadMaterialCertificateFile: builder.mutation({
			query: ({ id, formData }) => ({
				url: `material/materials/${id}/upload-certificate/`,
				method: 'POST',
				body: formData,
			}),
		}),

		setMaterialCertificateNotAvailable: builder.mutation({
			query: ({ id, value }: { id: string; value: boolean }) => ({
				url: `material/materials/${id}/certificate-not-available/`,
				method: 'POST',
				body: { value },
			}),
		}),

		deleteMaterialCertificateFile: builder.mutation({
			query: ({ id }) => ({
				url: `material/material-certificate-files/${id}/`,
				method: 'DELETE',
			}),
		}),

		createMaterialRemnant: builder.mutation({
			query: ({ id, values }) => ({
				url: `material/materials/${id}/create-remnant/`,
				method: 'POST',
				body: values,
			}),
		}),

		calculateRequiredLengths: builder.mutation({
			query: (values: CalculateRequiredLengthsProps) => ({
				url: `material/calculate-required-lengths/`,
				method: 'POST',
				body: values,
			}),
		}),

		calculateScrapQuantity: builder.mutation<
			CalculateScrapQuantityResult,
			CalculateScrapQuantityProps
		>({
			query: (values: CalculateScrapQuantityProps) => ({
				url: `material/calculate-scrap-quantity/`,
				method: 'POST',
				body: values,
			}),
		}),

		printCutTag: builder.mutation({
			query: (values) => ({
				url: `material/material-reservations/print-cut-tag/`,
				method: 'POST',
				body: values,
			}),
		}),

		printReceivingTagById: builder.mutation({
			query: ({ id, building_id }: { id: string; building_id?: string }) => ({
				url: `/material/material-received/${id}/print-tag/`,
				method: 'POST',
				body: building_id ? { building_id } : undefined,
			}),
		}),

		printMaterialLocationSummary: builder.mutation({
			query: (body) => ({
				url: `material/material-lengths/print-tag/`,
				method: 'POST',
				body,
			}),
		}),

		previewMaterialLocationSummary: builder.mutation({
			query: (body) => ({
				url: `material/material-lengths/preview-tag/`,
				method: 'POST',
				body,
				responseHandler: async (response) => {
					if (response.status === 200) {
						const blob = await response.blob();
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', 'material_location_summary.pdf');
						document.body.appendChild(link);
						link.click();
						link.parentNode?.removeChild(link);
						window.URL.revokeObjectURL(url);
                        return { status: 'success' };
					} else {
                        const text = await response.text();
                        try {
                            const errorData = JSON.parse(text);
                            notification.error({
                                message: 'Error',
                                description: errorData.error || 'Failed to generate preview',
                                placement: 'topRight',
                            });
                        } catch {
                            notification.error({
                                message: 'Error',
                                description: 'Failed to generate preview',
                                placement: 'topRight',
                            });
                        }
                        return { error: 'Failed' };
					}
				},
				cache: 'no-cache',
			}),
		}),
		getMaterialDimensionRanges: builder.query({
			query: (params) => ({
				url: '/material/materials/dimension-ranges/',
				params: params,
			}),
		}),

		getAllMaterialLengths: builder.query({
			query: (params) => {
				const cleanParams = Object.fromEntries(
					Object.entries(params).filter(
						([_, v]) => v !== null && v !== undefined && v !== ''
					)
				);

				return {
					url: 'material/material-lengths/',
					method: 'GET',
					params: cleanParams,
				};
			},
		}),

		getMaterialLengthsTotalValue: builder.query({
			query: (params) => {
				const { page, page_size, ...rest } = params || {};
				const cleanParams = Object.fromEntries(
					Object.entries(rest).filter(
						([_, v]) => v !== null && v !== undefined && v !== ''
					)
				);
				return {
					url: 'material/material-lengths/total-value/',
					method: 'GET',
					params: cleanParams,
				};
			},
		}),

		getMaterialLengthDimensionRanges: builder.query({
			query: (params = {}) => {
				const searchParams = new URLSearchParams();

				Object.keys(params).forEach((key) => {
					const value = params[key];

					if (value === undefined || value === null || value === '') {
						return;
					}

					if (Array.isArray(value)) {
						value.forEach((item) => {
							if (item !== undefined && item !== null && item !== '') {
								searchParams.append(key, String(item));
							}
						});
					} else {
						searchParams.append(key, String(value));
					}
				});

				const queryString = searchParams.toString();
				return `material/material-lengths/dimension-ranges/${
					queryString ? '?' + queryString : ''
				}`;
			},
		}),

		getMaterialSources: builder.query<MaterialSource[], void>({
			query: () => 'material/material-sources/',
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetMaterialTypesQuery,
	useGetMaterialVendorsQuery,
	useGetMaterialShapesQuery,
	useGetMaterialCategoriesQuery,
	useGetMaterialRacksQuery,
	useGetMaterialShelvesQuery,
	useGetMaterialSourcesQuery,
	useGetMaterialOrdersQuery,
	useGetMaterialOrderByIdQuery,
	useGetMaterialGroupedByLengthQuery,
	useLazyGetMaterialOrderByIdQuery,
	useGetMaterialQuery,
	useAddMaterialOrderLineItemMutation,
	useAddMaterialOrderMutation,
	useAddMaterialOrderDraftMutation,
	useAddMaterialOrderConfirmationMutation,
	useUpdateMaterialOrderLineItemMutation,
	useGetMaterialDetailQuery,
	useSearchMaterialMutation,
	useAddMaterialRemnantMutation,
	useGetMaterialRemnantQuery,
	useLazyGetMaterialRemnantQuery,
	useGetPOPDFMutation,
	useUpdateMaterialOrderMutation,
	useDeleteMaterialOrderMutation,
	useGetMaterialsOrderSearchMutation,
	usePatchMaterialOrderMutation,
	useGetMaterialsQuery,
	useLazyGetMaterialByIdQuery,
	useGetMaterialByIdQuery,
	useGetUniqueInventoryMaterialHeightsQuery,
	useGetUniqueInventoryMaterialWidthsQuery,
	useGetUniqueInventoryMaterialOuterDiametersQuery,
	useGetUniqueInventoryMaterialInnerDiametersQuery,
	useGetUniqueInventoryMaterialWallThicknessesQuery,
	useAddMaterialMutation,
	useUpdateMaterialMutation,
	useReserveMaterialMutation,
	useGetMaterialLengthByIdQuery,
	useAddMaterialLengthsMutation,
	useUpdateMaterialLengthMutation,
	useDeleteMaterialLengthMutation,
	useRecordAttributionCostMutation,
	useGetMaterialReservationsQuery,
	useGetMaterialReservationByIdQuery,
	useLazyGetMaterialReservationByIdQuery,
	useGetGroupedMaterialReservationsQuery,
	useAddMaterialReservationMutation,
	useUpdateMaterialReservationMutation,
	useUpdateMaterialReservationPriorityMutation,
	useUpdateMaterialReservationCutStatusMutation,
	useUpdateMultipleMaterialReservationCutStatusesMutation,
	useDeleteMaterialReservationMutation,
	useUploadMaterialCertificateFileMutation,
	useSetMaterialCertificateNotAvailableMutation,
	useDeleteMaterialCertificateFileMutation,
	useCreateMaterialRemnantMutation,
	useCalculateRequiredLengthsMutation,
	useCalculateScrapQuantityMutation,
	useLazyGetMaterialReservationsQuery,
	useUpdateMultipleMaterialReservationsToCutStatusMutation,
	useToggleMergeMutation,
	useDeleteMaterialOrderLineItemMutation,
	useRemoveMaterialOrderLineItemFromDraftMutation,
	useAddMaterialOrderLineItemFromMaterialRequestMutation,
	useAddMaterialOrderLineItemFromVendorMaterialMutation,
	useUploadMaterialDocumentFileInMaterialOrderLineItemMutation,
	usePrintCutTagMutation,
	usePrintReceivingTagByIdMutation,
	useGetMaterialDimensionRangesQuery,
	useGetAllMaterialLengthsQuery,
	useGetMaterialLengthsTotalValueQuery,
	useGetMaterialLengthDimensionRangesQuery,
	useSplitMaterialLengthMutation,
	usePrintMaterialLocationSummaryMutation,
	usePreviewMaterialLocationSummaryMutation,
	useGetInventoryLocationsQuery,
	useLazyGetInventoryLocationsQuery,
	useLazyGetSplitLocationCandidatesQuery,
} = extendedApiSlice;
